import Tag from '@components/Tag';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { LegStatus } from '@api/Trinium/types';
import { useFetchLegQuery } from '@api/Trinium/Endpoints/Leg';
import LegOrderHeaderSkeleton from './LegOrderHeaderSkeleton';

import gStyles from '@/Global.module.css';
import styles from './OrderHeader.module.css';

interface LegOrderHeaderProps {
	terminalNumber: string;
	legId?: number;
}

/**
 * Modal sub-header to display Leg Order Information
 */
export default function LegOrderHeader(props: LegOrderHeaderProps) {
	const { data: leg, isFetching } = useFetchLegQuery(
		{
			terminalNumber: props.terminalNumber,
			id: Number(props.legId)
		},
		{
			skip: !props.legId
		}
	);

	if (isFetching || !leg) {
		return <LegOrderHeaderSkeleton />;
	}

	return (
		<div className={`${styles.header} ${styles['leg-header']}`}>
			<span className={`${gStyles['label']} ${styles['order-lbl']}`}>Order #</span>
			<div className={styles.order}>
				<span className={gStyles.bold}>{leg.jobId.toString()}</span>
				<span className={gStyles.secondary}>{`/${leg.csn.no.toString().padStart(3, '0')}`}</span>
			</div>

			<span className={`${gStyles['label']} ${styles['customer-ref-lbl']}`}>Customer Ref #</span>
			<span
				className={`${gStyles.bold} ${styles['customer-ref']}`}
				title={leg.csn.custRefNo}
			>
				{leg.csn.custRefNo ? leg.csn.custRefNo : '-'}
			</span>

			<span className={`${gStyles['label']} ${styles['job-cat-lbl']}`}>Job Category</span>
			<span
				className={`${gStyles.bold} ${styles['job-cat']}`}
				title={leg.job?.cat ? `${leg.job.catTransType} (${leg.job.cat})` : '-'}
			>
				{leg.job?.cat ? `${leg.job.catTransType} (${leg.job.cat})` : '-'}
			</span>

			<div className={styles['tag-group']}>
				<Tag
					text='LIVE'
					tagStyle={leg.csn.isLive ? 'live' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='HAZ'
					tagStyle={leg.isHazmat ? 'haz' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='WGT'
					tagStyle={leg.csn.isOverweight ? 'wgt' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='BULK'
					tagStyle={leg.csn.isBulk ? 'bulk' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>
			</div>

			<span className={`${gStyles['label']} ${styles['leg-label']}`}>Leg</span>

			<span
				className={`${styles['leg']} ${gStyles.bold}`}
				title={`${String(leg.no)} - ${String(leg.type)}`}
			>
				{`${String(leg.no)} - ${String(leg.type)}`}
			</span>

			<span className={`${gStyles['label']} ${styles['status-lbl']}`}>Status</span>
			<Tag
				text={getEnumValueByKey(LegStatus, leg.status)}
				tagStyle={leg.status}
				className={styles.status}
			/>

			<span className={`${gStyles['label']} ${styles['assigned-lbl']}`}>
				{leg.drv?.type === 'D' ? 'Driver' : leg.drv?.type === 'B' ? 'Broker' : 'Assigned'}
			</span>
			<span
				className={`${styles.assigned} ${gStyles.bold}`}
				title={`${leg.drv?.name ?? ''} ${leg.drv?.type === 'D' ? (leg.drvId ?? '') : ''}`}
			>
				{leg.drv?.type === 'D' ? `${leg.drv.name ?? ''} (${leg.drvId ?? ''})` : leg.drv?.type === 'B' ? leg.drv.name : 'Unassigned'}
			</span>

			<span className={`${gStyles['label']} ${styles['tractor-lbl']}`}>Truck</span>
			<span
				className={`${styles.tractor} ${styles.bold}`}
				title={leg.tractorId ?? '-'}
			>
				{leg.tractorId ?? '-'}
			</span>
		</div>
	);
}
